/**
 * Effort — display and selection helpers. Frontend only.
 *
 * See `Planning/EFFORT-TRACKING-2026-09-01.md` (T468).
 *
 * ⛔ DELIBERATELY SEPARATE FROM `effort.js`. That file is a BYTE-IDENTICAL PAIR
 * with `lambda/layer/nodejs/effort.js` and a parity test fails on any
 * divergence — so a label format or a React-shaped selector put in there breaks
 * the backend copy or the parity check, whichever gets edited second. Nothing in
 * this file has a backend counterpart and nothing in it ever should.
 *
 * ⚠️ ADMIN-ONLY DATA (D4). None of these functions enforces that; the
 * enforcement is the server-side strip (T466) plus each component's own admin
 * gate. If a value reaches here at all, something upstream has already decided
 * it may be shown.
 */

import { formatEffort } from './effort';

/** What renders in place of a number that is not there. */
const DASH = '-';

/**
 * ⛔ IS THIS THE CLIENT'S OWN WORK? Effort never appears on it and never counts
 * towards a total (D1, 2026-09-02). Effort measures OUR delivery; a client-lane
 * task is theirs, and counting it inflates every figure we quote ourselves.
 *
 * ⚠️ MIRRORS THE DOZEN INLINE COPIES IN SHAPE, NOT IN COMPARISON
 * (`KanbanBoard.js:855`, `ClientView.js:2118`, `TaskDetailsModal.js:634`).
 *
 *   - MIRRORED: the two arms and their order — an explicit lane match, else the
 *     `default_swimlane` arm for a null lane. That arm is dead for every project
 *     this system creates (no Lambda writes the field) and is kept so a task
 *     cannot be a client task for permissions and not for effort.
 *   - STRICTER: the comparison itself is `String()`-coerced, and the inline
 *     copies' `===` is not. See the body — this catches a case they miss, and
 *     it fails towards HIDING effort, which is the safe direction under D1.
 *
 * ⚠️ THE FIRST LINE IS LOAD-BEARING. Without it, a project with no swimlanes
 * gives `teamSwimlaneId === undefined`, a task with no lane gives
 * `swimlaneId === undefined`, and `undefined === undefined` classifies EVERY
 * task as the client's — the whole feature would vanish on such a project.
 */
export function isClientLaneTask(task, teamSwimlaneId, project) {
    if (teamSwimlaneId === undefined || teamSwimlaneId === null) return false;
    if (!task) return false;
    if (task.swimlaneId === null || task.swimlaneId === undefined) {
        return teamSwimlaneId === project?.default_swimlane;
    }
    // ⛔ STRING-COMPARED, AND DELIBERATELY STRICTER THAN THE INLINE COPIES.
    // A task created through the admin MCP stores its lane as a STRING: the
    // tool schema types swimlaneId `string` (tools.js:2198), the commit spreads
    // the args straight in (`createTaskDynamo(a[idKey], { ...a, ...pair })`,
    // :2488), and taskWrite stores `swimlaneId || undefined` (:322) - no
    // coercion anywhere on that path. So such a row carries "2" where the
    // project's lane carries 2, and `===` answers false.
    //
    // Every BACKEND half of this plan already coerces (inTeamSwimlane,
    // stripClientLaneEffort, addEffort - F3), so they refuse and strip such a
    // row correctly. A frontend predicate using `===` would disagree with them:
    // coreMainTasks would KEEP the task, every rollup would count it as
    // unestimated - the precise failure §4.1 gate 3 exists to prevent - and D1
    // would not be met for MCP-created client-lane tasks.
    //
    // ⚠️ That string ids are real, not hypothetical, is corroborated by two
    // places that coerce defensively on their own writes:
    // admin-project-addins:261 and admin-templates:686 both write
    // `Number(...swimlaneId)`.
    return String(task.swimlaneId) === String(teamSwimlaneId);
}

/**
 * The Gantt bar suffix (D15): `(Est.4d · Act.6.5d)`.
 *
 * Brackets appear when AT LEAST ONE value is set; the missing side shows a dash,
 * so a task with an estimate and no logged time stays visible as
 * `(Est.4d · Act.-)` rather than disappearing. Both absent renders NOTHING —
 * unestimated bars stay clean.
 *
 * ⚠️ `estimateOnly` IS FOR TEMPLATES (D5). A template is never worked on, so
 * every bar would otherwise read `· Act.-` — noise on every row, saying only
 * that templates work the way templates work.
 *
 * ⚠️ THE MIDDLE DOT IS BILL'S (2026-09-01, "use the dots"). A hyphen between two
 * numbers reads as a range or a subtraction.
 *
 * @returns {string|null} the suffix, or null when there is nothing to say
 */
export function effortLabel(estimated, actual, { estimateOnly = false } = {}) {
    const est = formatEffort(estimated);

    if (estimateOnly) {
        return est === null ? null : `(Est.${est})`;
    }

    const act = formatEffort(actual);
    if (est === null && act === null) return null;

    return `(Est.${est ?? DASH} · Act.${act ?? DASH})`;
}

/**
 * Is this over? (D16/D24.)
 *
 * ⚠️ BOTH VALUES MUST BE PRESENT. An actual with no estimate is not an overrun —
 * there is nothing to be over. Reporting one would paint every unestimated task
 * red the moment any time was logged against it.
 *
 * Strictly greater: exactly on the estimate is not an overrun.
 */
export function isOverrun(estimated, actual) {
    if (typeof estimated !== 'number' || typeof actual !== 'number') return false;
    if (!Number.isFinite(estimated) || !Number.isFinite(actual)) return false;
    return actual > estimated;
}

/**
 * The variance: `actual − the TYPED estimate at this level` (D24).
 *
 * Bill, 2026-09-01: *"if we actually go over that's what we need to know"*. So
 * the comparison is against what was QUOTED, never against the sum of the main
 * tasks' own estimates — that sum is displayed beside it and is deliberately
 * allowed to disagree.
 *
 * ⛔ NULL WHEN EITHER SIDE IS ABSENT, and that is the whole point of returning a
 * number-or-null rather than a number. Zero would paint an overrunning epic
 * green, and falling back to the sum would silently answer a different question.
 */
export function variance(typedEstimate, actual) {
    if (typeof typedEstimate !== 'number' || !Number.isFinite(typedEstimate)) return null;
    if (typeof actual !== 'number' || !Number.isFinite(actual)) return null;
    // Subtracted in quarters, then converted back: every legal value is a whole
    // number of quarters, so this cannot produce 4.499999999999999.
    return (Math.round(actual * 4) - Math.round(typedEstimate * 4)) / 4;
}

/**
 * The variance as it renders: `+4`, `-1.25`, `0`, or a dash when there is no
 * typed estimate to compare against.
 */
export function varianceLabel(typedEstimate, actual) {
    const value = variance(typedEstimate, actual);
    if (value === null) return DASH;
    if (value > 0) return `+${value}`;
    return String(value);
}

/**
 * ⛔ THE ONE SELECTOR EVERY ROLLUP USES. Kanban column headers (T470), the Epics
 * section (T473), the project line (T474) and the Template Editor (T477) all
 * call this, so two surfaces cannot disagree about the same epic on one screen.
 *
 * It returns MAIN TASKS, and applies exactly two exclusions:
 *
 *   - ADD-IN TASKS ARE EXCLUDED (D23). The typed estimate is a commitment about
 *     the core scope; an add-in attached later would otherwise read as an
 *     instant overrun against it.
 *   - CLIENT-SWIMLANE TASKS ARE EXCLUDED (D1, 2026-09-02). Effort measures OUR
 *     delivery. They are EXCLUDED rather than merely stripped of their numbers,
 *     because `sumEffort` reports `{ total, unestimated }` — a stripped row
 *     would still inflate the amber "N unestimated" badge on every epic.
 *
 * and deliberately applies none of these:
 *
 *   - NOT filtered by hide-completed, lane filter or view mode (D17). A total
 *     that silently drops rows is how you under-quote.
 *   - NOT filtered by whether the task has subtasks (D22). The Epics section
 *     hides those rows, and hides a whole epic whose main tasks are ALL
 *     subtask-less — but their effort still counts, so the project total is
 *     computed from this list rather than by adding up what is on screen.
 *
 * ⚠️ THE ADD-IN TEST IS `sourceAddinId`, NOT `_isAddin`. `_isAddin` is a
 * frontend-only derivation computed in `TemplateEditor.js` and forwarded by
 * `GanttRowBuilder.js`; `ClientView.js` never sets it. A filter written against
 * it would exclude NOTHING in the admin client view — while passing any test
 * whose fixture happened to set the flag.
 *
 * @param {Array<Object>} tasks       every task in the project
 * @param {Object} [options]
 * @param {number|string} [options.epicId]  limit to one epic; omit for the project
 * @param {number|string|null} [options.teamSwimlaneId] the client lane's id;
 *        `null` when the project has no lanes. See the note in the body.
 * @param {Object} [options.project]  the project row, for the `default_swimlane` arm
 * @returns {Array<Object>} the main tasks whose effort counts
 */
export function coreMainTasks(tasks, { epicId, teamSwimlaneId, project } = {}) {
    // ⛔ EVERY CALL SITE MUST PASS `teamSwimlaneId` - `null` when the project has
    // no lanes (D1). It is NOT enforced here, deliberately (D9, Bill
    // 2026-09-02): throwing would break the page on screen, so the guard is a
    // TEST that scans the call sites and fails the build instead - see §7.1.
    // Omitting it silently excludes nothing, which is the original defect, so
    // that test is the only thing standing between here and its return.
    return (tasks || []).filter(task => {
        if (!task) return false;
        // A main task is one with no parent. `type` is the stored
        // discriminator; `mainTaskId` is checked too because several frontend
        // paths build rows carrying only the latter.
        const isMainTask = task.type === 'maintask' || (!task.mainTaskId && task.type !== 'subtask');
        if (!isMainTask) return false;
        if (task.sourceAddinId) return false;
        // D1 - the client's own work is not ours to estimate or count.
        if (isClientLaneTask(task, teamSwimlaneId, project)) return false;
        if (epicId !== undefined && epicId !== null) {
            return String(task.epicId) === String(epicId);
        }
        return true;
    });
}
